<template>
  <utd-modal-dialog ref="modalRef"
                    :modal-classes="'post-reg-profile-modal'"
                    :close-on-overlay-click="false"
                    :hide-header-close="true"
                    @closed="handleClose()">
    <template #header>Please complete your profile</template>

    <div class="post-reg-profile-dialog">
      <utd-notification v-if="submitSuccess"
                        :notification-mode="'success'">
        <template #title>Success</template>
        <div>Profile updated successfully</div>
      </utd-notification>

      <utd-notification v-if="hasError"
                        :notification-mode="'error'">
        <template #title>Error</template>
        <div>{{ $t('MYACCOUNT.UPDATE_INFORMATION_UNEXPECTED_ERROR') }}</div>
      </utd-notification>

      <form class="profile-form">
        <fieldset v-for="field in visibleFields"
                  :key="field.name"
                  class="profile-form__fieldset wk-js2">
          <utd-text-input v-if="field.type === 'TEXTBOX'"
                          :label="$t(getLabelForField(field))"
                          :is-error="!isFieldValid(field.name) && isDirty[field.name]"
                          :error-message="$t(
                            getFieldError(field.name), 
                            { maxLength: parseInt(field.maxLength, 10) }
                          )"
                          :required="true"
                          :input-id="`postReg-${field.name}`">
            <input :id="`postReg-${field.name}`"
                   v-model="fields[field.name]"
                   class="wk-field-input"
                   :disabled="!field.isEditable"
                   @blur="handleInputBlur(field.name)">
          </utd-text-input>

          <utd-select v-else-if="field.type === 'DROPDOWN'"
                      :class="`${field.name}Select`"
                      :options="getOptionsForField(field.name)"
                      :selected="selectedIndices[field.name]"
                      placeholder="Select an option"
                      :label="getLabelForField(field)"
                      :required="field.required"
                      :is-disabled="!field.isEditable"
                      @utd-select="handleSelect(field.name, $event)" />
        </fieldset>
      </form>
    </div>

    <template #footer>
      <utd-button v-if="!submitSuccess"
                  :button-style="'ghost'"
                  @click.prevent="askAgainTomorrow()">
        Ask Again Tomorrow
      </utd-button>
      <utd-button v-if="!submitSuccess"
                  :disabled="isSubmitting || !isFormValid"
                  @click.prevent="submitForm()">
        Submit
      </utd-button>
    </template>
  </utd-modal-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import UtdSelect from '_acaSrc/components/shared/input/UtdSelect.vue';
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import { C_EVENTS, validZipCodeRegex, validMailingAddressRegex, 
    ZIPCODE_FETCH_SUCCESS } from '_acaSrc/utility/constants';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import PubSub from '_acaSrc/utility/PubSub';
import zipLocationService from '_acaSrc/composables/zipLocationService.composable';

const store = useStore();

// Form State/Validation
const formFields = ref([]);
const isInternational = ref(true);

const visibleFields = computed(() => {
    return formFields.value.filter(field => {
        if (field.isHidden) {
            return false;
        }

        if (field.name === 'city' && !isInternational.value) {
            return false;
        }

        if (field.name === 'zip' && isInternational.value) {
            return false;
        }

        return true;
    });
});

const myAccountPracticeSettingOptions = computed(
    () => store.getters['account/myAccountPracticeSettingOptions']
);

const myAccountpracticeTypeOptions = computed(
    () => store.getters['account/myAccountpracticeTypeOptions']
);

const myAccountSpecialtyOptions = computed(
    () => store.getters['account/myAccountSpecialtyOptions']
);

function getOptionsForField(key) {
    const optionsMap = {
        practiceSetting: myAccountPracticeSettingOptions.value,
        practiceType: myAccountpracticeTypeOptions.value,
        specialty: myAccountSpecialtyOptions.value
    };
    return optionsMap[key] || [];
}

const fieldConfig = {
    practiceSetting: {
        label: 'Practice Setting'
    },
    practiceType: {
        label: 'Role'
    },
    specialty: {
        label: 'Specialty'
    },
    zip: {
        label: 'MYACCOUNT.CONTACT_ZIP',
        regex: validZipCodeRegex,
        errorKey: 'MYACCOUNT.INVALID_ZIPCODE'
    },
    city: {
        label: 'MYACCOUNT.CONTACT_CITY',
        regex: validMailingAddressRegex,
        errorKey: 'MYACCOUNT.INVALID_CITY'
    }
};

function getLabelForField(field) {
    const config = fieldConfig[field.name];
    if (config?.label) {
        return config?.label.startsWith('MYACCOUNT.') ? config.label : config.label;
    }
    return field.label;
}

const fields = reactive({});
const selectedIndices = reactive({});
const isDirty = reactive({});
const serverErrors = reactive({});

function initFields() {
    visibleFields.value.forEach(field => {
        fields[field.name] = field.value || '';
        isDirty[field.name] = false;
        if (field.type === 'DROPDOWN') {
            selectedIndices[field.name] = null;
        }
    });
}

function handleInputBlur(fieldName) {
    isDirty[fieldName] = true;
    serverErrors[fieldName] = '';
    if (fieldName === 'zip' && !isInternational.value) {
        validateZipCode();
    }
}

async function validateZipCode() {
    const zipValue = fields.zip;
    if (!zipValue || isInternational.value) {
        return;
    }
    try {
        const { status } = await zipLocationService().fetchCityStateFromZip(zipValue);
        if (status === ZIPCODE_FETCH_SUCCESS) {
            serverErrors.zip = '';
        }
        else {
            serverErrors.zip = 'MYACCOUNT.INVALID_ZIPCODE';
        }
    }
    catch {
        serverErrors.zip = 'MYACCOUNT.INVALID_ZIPCODE';
    }
}

function handleSelect(key, value) {
    if (value < 0) {
        return;
    }
    const options = getOptionsForField(key);
    fields[key] = options[value].value;
    selectedIndices[key] = value;
}

function isFieldValid(fieldName) {
    const fieldDef = visibleFields.value.find(f => f.name === fieldName);
    if (!fieldDef || fieldDef.type !== 'TEXTBOX') {
        return true;
    }
    return getFieldError(fieldName) === '';
}

function isFieldComplete(fieldName) {
    const fieldDef = visibleFields.value.find(f => f.name === fieldName);
    if (!fieldDef) {
        return true;
    }
    const value = fields[fieldName];
    if (fieldDef.type === 'DROPDOWN') {
        return selectedIndices[fieldName] !== null && selectedIndices[fieldName] >= 0;
    }
    return value && value.length > 0;
}

function validateRequired(fieldDef, value) {
    if (!value.length) {
        return 'MYACCOUNT.EMPTY_FIELD';
    }
    return '';
}

function validateMaxLength(fieldDef, value) {
    const maxLength = parseInt(fieldDef.maxLength, 10);
    if (maxLength && value.length > maxLength) {
        return 'MYACCOUNT.INVALID_FIELD_LENGTH';
    }
    return '';
}

function validatePattern(fieldDef, value) {
    const config = fieldConfig[fieldDef.name];
    if (config?.regex && value.length) {
        if (!value.match(config?.regex)) {
            return config.errorKey;
        }
    }
    return '';
}

function getFieldError(fieldName) {
    const fieldDef = visibleFields.value.find(f => f.name === fieldName);
    if (!fieldDef || fieldDef.type !== 'TEXTBOX') {
        return '';
    }
    const value = fields[fieldName] || '';
    
    return serverErrors[fieldName]
        || validateRequired(fieldDef, value) 
        || validateMaxLength(fieldDef, value)
        || validatePattern(fieldDef, value);
}

const isFormValid = computed(() => {
    return visibleFields.value.every(field => 
        isFieldValid(field.name) && isFieldComplete(field.name)
    );
});

// Form Submission
const isSubmitting = ref(false);
const submitSuccess = ref(false);
const hasError = ref(false);

const userProfessionFields = [ 'practiceSetting', 'practiceType', 'specialty' ];

function buildSubmitPayload() {
    const payload = {
        contactInfo: { address: {} },
        userProfession: {}
    };

    visibleFields.value.forEach(field => {
        if (userProfessionFields.includes(field.name)) {
            payload.userProfession[field.name] = fields[field.name];
        }
        else {
            payload.contactInfo.address[field.name] = fields[field.name];
        }
    });
    return payload;
}

async function submitForm() {
    visibleFields.value.forEach(field => {
        isDirty[field.name] = true;
    });
    if (!isFormValid.value) {
        return;
    }
    try {
        isSubmitting.value = true;
        hasError.value = false;
        const payload = buildSubmitPayload();
        await utdRest('profile/modal/submit', payload);
        submitSuccess.value = true;
        modalRef.value.close();
    }
    catch {
        hasError.value = true;
    }
    finally {
        isSubmitting.value = false;
    }
}

// Modal Control
const modalRef = ref(null);
const isModalLoading = ref(false);
const isLoggedIn = computed(() => store.getters['profile/userProfileVisitor'].isLoggedIn);
const isUccState = computed(() => store.getters['app/isUccState']);

function shouldOpenModal(response) {
    return response?.profileModalForm?.isSuccess !== false;
}

function initializeModal(response) {
    if (!shouldOpenModal(response)) {
        return;
    }
    formFields.value = response?.profileModalForm?.formFields || [];
    isInternational.value = response?.isInternational ?? true;
    initFields();
    modalRef.value.open();
}

async function showModal() {
    if (isModalLoading.value || !isLoggedIn.value || isUccState.value) {
        return;
    }
    try {
        isModalLoading.value = true;
        const response = await utdRest('profile/modal');
        initializeModal(response);
    }
    catch {
        // No-op
    }
    finally {
        isModalLoading.value = false;
    }
}

function handleClose() {
    submitSuccess.value = false;
    hasError.value = false;
    visibleFields.value.forEach(field => {
        isDirty[field.name] = false;
    });
}

function askAgainTomorrow() {
    modalRef.value.close();
}

// Event Subscription
const pendingOpen = ref(false);
const isAppDataLoaded = computed(() => store.getters['app/isAppDataLoaded']);

const canShowModal = computed(() => isAppDataLoaded.value && isLoggedIn.value && !isUccState.value);

function handleShowEvent() {
    if (!canShowModal.value) {
        pendingOpen.value = true;
        return;
    }
    showModal();
}

watch(canShowModal, (newVal) => {
    if (newVal && pendingOpen.value) {
        pendingOpen.value = false;
        showModal();
    }
});

onMounted(() => {
    new PubSub().subscribe(C_EVENTS.SHOW_POST_REG_MODAL, handleShowEvent);
});

onBeforeUnmount(() => {
    new PubSub().unsubscribe(C_EVENTS.SHOW_POST_REG_MODAL, handleShowEvent);
});
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.post-reg-profile-dialog {
    :deep(.utd-notification) {
        .ds1-mt-0();
        .ds1-mb-2();
    }
}

.profile-form__fieldset {
    .ds1-ma-0();
    .ds1-pa-0();
    border: none;

    &:has(#postReg-zip) {
        :deep(.wk-field-input) {
            max-width: 150px;
        }
    }
}

</style>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.post-reg-profile-modal .utd-dialog-content {
  .ds1-pa-4();
}

</style>
